import { BElement } from "utils/base-component"

export class MobileDropdowns {
  private cssClass: string
  private weatherBtn?: BElement
  private weatherDropdown?: BElement
  private newsBtn?: BElement
  private newsDropdown?: BElement
  private mySnowBtn?: BElement
  private mySnowDropdown?: BElement
  private accountBtn?: BElement
  private accountDropdown?: BElement
  private settingsBtn?: BElement
  private settingsDropdown?: BElement

  constructor(cssClass: string) {
    this.cssClass = cssClass
    this.initializeElements()
    this.initializeDropdownStates()
    this.addEventListeners()
  }

  private initializeElements() {
    const getElement = (selector: string) => {
      const element = document.querySelector(selector)
      return element ? new BElement(element, this.cssClass) : undefined
    }

    this.weatherBtn = getElement(".menu-nav__button--weather")
    this.weatherDropdown = getElement(".menu-nav__weather-dropdown")
    this.newsBtn = getElement(".menu-nav__button--news")
    this.newsDropdown = getElement(".menu-nav__news-dropdown")
    this.mySnowBtn = getElement(".menu-nav__button--mysnow")
    this.mySnowDropdown = getElement(".menu-nav__mysnow-dropdown")
    this.accountBtn = getElement(".menu-nav__button--account")
    this.accountDropdown = getElement(".menu-nav__account-dropdown")
    this.settingsBtn = getElement(".menu-nav__button--settings")
    this.settingsDropdown = getElement(".menu-nav__settings-dropdown")
  }

  private initializeDropdownStates() {
    // Set initial dropdown state
    this.weatherBtn?.element.setAttribute("aria-expanded", "true")
    this.weatherDropdown?.element.classList.add("active")
    this.newsBtn?.element.setAttribute("aria-expanded", "false")
    this.mySnowBtn?.element.setAttribute("aria-expanded", "false")
    this.accountBtn?.element.setAttribute("aria-expanded", "false")
    this.settingsBtn?.element.setAttribute("aria-expanded", "false")
  }

  private addEventListeners() {
    this.weatherBtn?.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleDropdown(this.weatherDropdown, this.weatherBtn)
    })

    this.newsBtn?.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleDropdown(this.newsDropdown, this.newsBtn)
    })

    this.mySnowBtn?.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleDropdown(this.mySnowDropdown, this.mySnowBtn)
    })

    this.accountBtn?.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleDropdown(this.accountDropdown, this.accountBtn)
    })

    this.settingsBtn?.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleDropdown(this.settingsDropdown, this.settingsBtn)
    })
  }

  private toggleDropdown(dropdown?: BElement, button?: BElement): void {
    if (!dropdown?.element || !button?.element) return

    const dropdownElement = dropdown.element
    const buttonElement = button.element
    const isExpanded = buttonElement.getAttribute("aria-expanded") === "true"

    // first close all dropdowns
    this.closeAllDropdowns(dropdown)

    // Toggle dropdown state
    if (isExpanded) {
      dropdownElement.classList.remove("active")
      buttonElement.setAttribute("aria-expanded", "false")
      const arrowIcon = buttonElement.querySelector(".menu-nav__down-arrow-icon")
      if (arrowIcon) {
        arrowIcon.classList.remove("active")
      }
    } else {
      dropdownElement.classList.add("active")
      buttonElement.setAttribute("aria-expanded", "true")
      const arrowIcon = buttonElement.querySelector(".menu-nav__down-arrow-icon")
      if (arrowIcon) {
        arrowIcon.classList.add("active")
      }
    }
  }

  // Public method to check if click is inside dropdowns
  isDropdownClick(target: Node): boolean {
    return !!(
      (this.weatherDropdown?.element && this.weatherDropdown.element.contains(target)) ||
      (this.newsDropdown?.element && this.newsDropdown.element.contains(target)) ||
      (this.mySnowDropdown?.element && this.mySnowDropdown.element.contains(target)) ||
      (this.accountDropdown?.element && this.accountDropdown.element.contains(target)) ||
      (this.settingsDropdown?.element && this.settingsDropdown.element.contains(target))
    )
  }

  // Public method to check if click is on dropdown buttons
  isDropdownButtonClick(target: Node): boolean {
    return !!(
      (this.weatherBtn?.element && this.weatherBtn.element.contains(target)) ||
      (this.newsBtn?.element && this.newsBtn.element.contains(target)) ||
      (this.mySnowBtn?.element && this.mySnowBtn.element.contains(target)) ||
      (this.accountBtn?.element && this.accountBtn.element.contains(target)) ||
      (this.settingsBtn?.element && this.settingsBtn.element.contains(target))
    )
  }

  // Public method to close all dropdowns
  closeAllDropdowns(exceptDropdown?: BElement): void {
    const dropdowns = [
      { dropdown: this.weatherDropdown, button: this.weatherBtn },
      { dropdown: this.newsDropdown, button: this.newsBtn },
      { dropdown: this.mySnowDropdown, button: this.mySnowBtn },
      { dropdown: this.accountDropdown, button: this.accountBtn },
      { dropdown: this.settingsDropdown, button: this.settingsBtn },
    ]

    dropdowns.forEach(({ dropdown, button }) => {
      if (exceptDropdown && dropdown === exceptDropdown) return

      if (dropdown?.element && button?.element) {
        if (dropdown.element.classList.contains("active")) {
          dropdown.element.classList.remove("active")
          button.element.setAttribute("aria-expanded", "false")
        }

        const arrowIcon = button.element.querySelector(".menu-nav__down-arrow-icon")
        if (arrowIcon) {
          arrowIcon.classList.remove("active")
        }
      }
    })
  }

  // Public method to check if any mobile dropdowns are currently open
  hasOpenDropdowns(): boolean {
    return !!(
      (this.weatherDropdown?.element &&
        this.weatherDropdown.element.classList.contains("active")) ||
      (this.newsDropdown?.element && this.newsDropdown.element.classList.contains("active")) ||
      (this.mySnowDropdown?.element && this.mySnowDropdown.element.classList.contains("active")) ||
      (this.accountDropdown?.element &&
        this.accountDropdown.element.classList.contains("active")) ||
      (this.settingsDropdown?.element && this.settingsDropdown.element.classList.contains("active"))
    )
  }
}
